import {useReservationDisplay} from '../../lib/reservationDisplayPreference';
import React, { useEffect, useId, useRef, useState } from 'react';
import SegmentedSwitch3D from '../ui/SegmentedSwitch3D';
import { useSaveToast } from '../ui/SaveToast';
import {
    DEFAULT_ENTER_KEY, ENTER_KEY_HELP, ENTER_KEY_NEWLINE, ENTER_KEY_SEND, loadEnterKeyPreference, saveEnterKeyPreference,
} from '../../lib/enterKeyPreference';
import {
    DEFAULT_MENU_OPEN, MENU_OPEN_CLICK, MENU_OPEN_HOVER, useMenuOpensPreference,
} from '../../lib/menuOpenPreference';

// Settings > Communications > Typing & sending: ONE two-position 3D switch, "Enter key: Send Message / Line break" (Val-approved direction).
//   Send Message (default): Enter sends, Shift+Enter adds a line break.   Line break: Enter adds a line break, Shift+Enter sends.
//   Cmd/Ctrl+Enter always sends, in both positions. Email composers keep Enter as a new line whichever way this is set.
// It lives ONLY here (the person's own settings); the messaging screens read it (EnterKeyPreferenceContext) but never show or
// change it. Stored exactly as before ('send' | 'newline' on the person's own profile), so it follows them across computer and
// phone and applies the next time a composer opens.
export default function EnterKeyPreferenceCard({ client, userId }) {
    const toast = useSaveToast();
    const ids = useId();
    const [mode, setMode] = useState(DEFAULT_ENTER_KEY);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const saved = useRef(DEFAULT_ENTER_KEY);
    const menuPreference = useMenuOpensPreference(userId, client);
    const reservationDisplay=useReservationDisplay(client,userId);

    useEffect(() => {
        let active = true;
        if (!client || !userId) return () => { active = false; };
        loadEnterKeyPreference(client, userId)
            .then((value) => { if (active) { saved.current = value; setMode(value); } })
            .catch(() => { /* keep the default (Enter sends) when the saved choice cannot be read */ })
            .finally(() => { if (active) setLoaded(true); });
        return () => { active = false; };
    }, [client, userId]);

    async function choose(enterSends) {
        const next = enterSends ? ENTER_KEY_SEND : ENTER_KEY_NEWLINE;
        if (next === mode) return;
        setMode(next);
        setError('');
        try {
            const value = await saveEnterKeyPreference(client, next);
            saved.current = value;
            toast.saved();
        } catch (failure) {
            setMode(saved.current);
            const message = failure?.message || 'Could not save your Enter key choice.';
            setError(message);
            toast.failed(message);
        }
    }

    async function chooseMenuOpen(value) {
        try {
            await menuPreference.save(value);
            toast.saved();
        } catch (failure) {
            toast.failed(failure?.message || 'Could not save your menu preference.');
        }
    }

    const enterSends = mode === ENTER_KEY_SEND;
    return (
        <section className="flex flex-col gap-3" aria-labelledby={`${ids}-title`} data-testid="enter-key-card">
            <div className="flex flex-wrap items-center gap-3 text-sm">
                <span id={`${ids}-title`} className="font-semibold">Enter key</span>
                <SegmentedSwitch3D
                    labelledBy={`${ids}-title`}
                    describedBy={`${ids}-help`}
                    value={loaded ? mode : undefined}
                    disabled={!loaded}
                    onChange={(value) => choose(value === ENTER_KEY_SEND)}
                    options={[{ value: ENTER_KEY_SEND, label: 'Send Message' }, { value: ENTER_KEY_NEWLINE, label: 'Line break' }]}
                    testId="enter-key-switch"
                />
            </div>
            <p id={`${ids}-help`} className="text-sm" data-testid="enter-key-help">
                {enterSends
                    ? 'Send Message: Enter sends the message and Shift+Enter adds a new line.'
                    : 'Line break: Enter adds a new line and Shift+Enter sends the message.'}
                {' '}{ENTER_KEY_HELP}
            </p>
            {error && <p role="alert" className="text-sm font-semibold">{error}</p>}
            <div className="flex flex-col gap-2 border-t border-[var(--surface-border)] pt-3" data-testid="menu-open-preference">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span id={`${ids}-menu-title`} className="font-semibold">Menu opens on</span>
                    <SegmentedSwitch3D
                        labelledBy={`${ids}-menu-title`}
                        describedBy={`${ids}-menu-help`}
                        value={menuPreference.loaded ? menuPreference.value || DEFAULT_MENU_OPEN : undefined}
                        disabled={!menuPreference.loaded || menuPreference.saving || !userId}
                        onChange={chooseMenuOpen}
                        options={[{ value: MENU_OPEN_HOVER, label: 'Hover' }, { value: MENU_OPEN_CLICK, label: 'Click' }]}
                        testId="menu-open-switch"
                    />
                </div>
                <p id={`${ids}-menu-help`} className="text-sm">
                    Hover opens menus as you point on desktop. Touch screens always use tap.
                </p>
                {menuPreference.error && <p role="alert" className="text-sm font-semibold">{menuPreference.error}</p>}
            </div>
            <div className="flex flex-col gap-2 border-t border-[var(--surface-border)] pt-3">
                <label>Reservation form<select aria-label="Reservation form" value={reservationDisplay.value} disabled={!userId} onChange={async(event)=>{try{await reservationDisplay.save(event.target.value);toast.saved();}catch{toast.failed('Could not save your reservation form preference.');}}}>
                    <option value="popup">Pop-up (solid)</option><option value="overlay">Overlay (see-through)</option>
                </select></label>
                {reservationDisplay.error&&<p role="alert">{reservationDisplay.error}</p>}
            </div>
        </section>
    );
}
